import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import './ClosedDoorCard.css';

/**
 * The closed-door reasons. Every terminal connection failure resolves to one
 * of these; the mapping from the session's `ErrorKind` lives in
 * `bootErrorCopy.closedDoorReason`, the copy in `errors.closedDoor.*` (i18n),
 * and the visual here. `connection === 'schemaMismatch'` maps to
 * `'schemaMismatch'` directly at the App level.
 */
export type ClosedDoorReason =
  | 'roomFull'
  | 'gameInProgress'
  | 'schemaMismatch'
  | 'couldntConnect'
  | 'accessDeclined'
  | 'outsideDiscord';

/**
 * Reasons whose recovery is a plain activity reload (which re-runs boot and
 * re-attempts the join). `accessDeclined` is intentionally absent: a reload
 * may not re-prompt Discord's consent, so it shows no action rather than a
 * button that silently does nothing. Verified-unknown until the real Activity
 * launch (LobbySession Epic 15 TODO); the body copy carries the instruction.
 * `outsideDiscord` is also absent: reloading a plain browser tab leaves it a
 * plain browser tab — the body copy points at Discord instead.
 */
const ACTION_REASONS = new Set<ClosedDoorReason>([
  'roomFull',
  'gameInProgress',
  'schemaMismatch',
  'couldntConnect',
]);

/**
 * The unified terminal connection-failure surface — a value-inverted "lights
 * out" screen. Where the warm chapbook means "you're in the game", this is its
 * inverse: a deep warm-ink ground with a pair of rough creature-eyes watching
 * from the dark, drawn in cream. Replaces the bare boot-failure text and the
 * old SchemaMismatchCard; renders any reason by `reason`.
 */
export function ClosedDoorCard({
  reason,
  onRetry = () => window.location.reload(),
}: {
  reason: ClosedDoorReason;
  onRetry?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const hasAction = ACTION_REASONS.has(reason);
  return (
    <main className="susdle-closeddoor">
      <div className="susdle-closeddoor__card" role="alert">
        <EyesSigil />
        <h1 className="susdle-closeddoor__title">{t(`errors.closedDoor.${reason}.title`)}</h1>
        <p className="susdle-closeddoor__body">{t(`errors.closedDoor.${reason}.body`)}</p>
        {hasAction ? (
          <button
            type="button"
            className="susdle-closeddoor__action"
            onClick={onRetry}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- closed-door is a terminal error screen; focusing the retry button is the only meaningful action and the surrounding card carries role="alert" so AT users hear it.
            autoFocus
          >
            {t(`errors.closedDoor.${reason}.action`)}
          </button>
        ) : null}
      </div>
    </main>
  );
}

/**
 * The eyes: the unseen creature, watching from the dark. A pair of rough,
 * pale-mottled eyes in the project's woodcut-sigil language. Two SVG filters
 * carry the look: `rough` (turbulence displacement) wobbles every edge so
 * nothing reads as a smooth vector, and `mottle` speckles warm-dark grain
 * across the pale. Decorative atmosphere — the message is the text, so this is
 * aria-hidden. Fills are token-driven via CSS (SVG attributes can't resolve
 * `var()`).
 */
function EyesSigil(): JSX.Element {
  return (
    <div className="susdle-closeddoor__eyes-wrap" aria-hidden>
      <svg
        className="susdle-closeddoor__eyes"
        viewBox="0 24 360 82"
        focusable={false}
        role="presentation"
      >
        <defs>
          <filter id="susdle-cd-rough" x="-25%" y="-25%" width="150%" height="150%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.08"
              numOctaves="3"
              seed="11"
              result="n"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="n"
              scale="7"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
          <filter id="susdle-cd-mottle" x="-10%" y="-10%" width="120%" height="120%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.5"
              numOctaves="2"
              seed="5"
              result="g"
            />
            <feColorMatrix
              in="g"
              type="matrix"
              values="0 0 0 0 0.12  0 0 0 0 0.10  0 0 0 0 0.08  0 0 0 0.4 0"
              result="speck"
            />
            <feComposite in="speck" in2="SourceGraphic" operator="in" result="clip" />
            <feMerge>
              <feMergeNode in="SourceGraphic" />
              <feMergeNode in="clip" />
            </feMerge>
          </filter>
        </defs>
        <g filter="url(#susdle-cd-rough)">
          {/* left eye — tilted counterclockwise */}
          <g transform="rotate(-10 80 65)">
            <path
              className="susdle-closeddoor__sclera"
              filter="url(#susdle-cd-mottle)"
              d="M 35 65 C 39.4 48.3 56.8 38.6 80 40 C 103.2 41.4 120.6 51.1 125 65 C 120.6 81.7 100.3 91.4 78.55 90 C 56.8 88.6 39.4 81.7 35 65 Z"
            />
            <circle className="susdle-closeddoor__pupil" cx="80" cy="65" r="9" />
          </g>
          {/* right eye — tilted clockwise */}
          <g transform="rotate(10 280 65)">
            <path
              className="susdle-closeddoor__sclera"
              filter="url(#susdle-cd-mottle)"
              d="M 325 65 C 320.6 48.3 303.2 38.6 280 40 C 256.8 41.4 239.4 51.1 235 65 C 239.4 81.7 259.7 91.4 281.45 90 C 303.2 88.6 320.6 81.7 325 65 Z"
            />
            <circle className="susdle-closeddoor__pupil" cx="280" cy="65" r="9" />
          </g>
        </g>
      </svg>
    </div>
  );
}
